import { useEffect, useMemo, useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { renderFilterErrorMessage, transformToSelectOptions } from '~/utils';

import { API_ENDPOINT, STOCK_CODES, STOCK_CODE_KEY, TypeStockOptionCodes, TypeStockOptions } from '~/constants';

import { TypeGetCodesParams, TypeGetCodesResponse, TypeGetVendorReleaseMethodCodesResponse } from '~/types/stock.types';

const useOption = ({ codes }: { codes: TypeStockOptionCodes[] }) => {
  const { runAllSettled } = useAsyncHandler();

  const [options, setOptions] = useState<TypeStockOptions | null>(null);
  const isRequestVendorReleaseMethod = useMemo(() => codes.includes(STOCK_CODES.vendorReleaseMethod), [codes]);

  const getCodes = (codes: TypeStockOptionCodes[]) =>
    codes.filter((optionCode) => Object.keys(STOCK_CODE_KEY).includes(optionCode));

  const formatCodes = (data: Partial<TypeGetCodesResponse['data']>): TypeStockOptions => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        STOCK_CODE_KEY[key as TypeStockOptionCodes],
        transformToSelectOptions(value, 'code', 'name'),
      ])
    ) as TypeStockOptions;
  };

  const getOptionCodes = useAPI<TypeGetCodesParams, TypeGetCodesResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_CODES,
    method: 'get',
  });

  const getVendorReleaseMethodCodes = useAPI<TypeGetCodesParams, TypeGetVendorReleaseMethodCodesResponse>({
    endpoint: API_ENDPOINT.STOCK.GET_VENDOR_RELEASE_METHOD_CODE,
    method: 'get',
  });

  const requestOptions = async (
    codes: string,
    {
      onSuccess,
    }: {
      onSuccess?: (data: TypeStockOptions) => void;
    } = {}
  ) => {
    const asyncFunctions: [
      () => Promise<TypeGetCodesResponse>,
      ...(() => Promise<TypeGetVendorReleaseMethodCodesResponse>)[]
    ] = [
      () => getOptionCodes({ params: { codes } }),
      ...(isRequestVendorReleaseMethod ? [() => getVendorReleaseMethodCodes({})] : []),
    ];

    runAllSettled(asyncFunctions, {
      onSuccess: (results) => {
        const [codesResult, methodCodeResult] = results;
        const isSuccessCodes = codesResult.status === 'fulfilled';
        const isSuccessMethodCodes = methodCodeResult?.status === 'fulfilled';

        const resultOptions = {
          ...(isSuccessCodes ? codesResult.value.data : {}),
          ...(isSuccessMethodCodes ? methodCodeResult.value.data : {}),
        };

        // 일부 코드 요청이 실패하는 경우에도 에러 처리
        if (!isSuccessCodes || !(isRequestVendorReleaseMethod ? isSuccessMethodCodes : true)) {
          toastService.addToast({
            type: 'error',
            message: renderFilterErrorMessage(),
          });
          return;
        }

        const options = formatCodes(resultOptions);
        setOptions(options);
        onSuccess?.(options);
      },
      onError: () => {
        toastService.addToast({
          type: 'error',
          message: renderFilterErrorMessage(),
        });
      },
    });
  };

  useEffect(() => {
    requestOptions(getCodes(codes).join(','));
  }, []);

  return {
    options,
    requestOptions,
  };
};

export default useOption;
